import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiConfig, apiClient } from '../config/api';
import AuthService from '../services/AuthService';
import { Event } from '../types/Event';
import { Headline } from '../types/Headline';
import { Reminder } from '../types/Reminder';
import { Achievement } from '../types/Achievement';
import { QuickAccess } from '../types/QuickAccess';
import { getLocationDisplayName, getLocationAddress, generateMapsUrl } from '../utils/locationUtils';
import { MapPin, Calendar, Plus } from 'lucide-react';
import ChoirDisplay from './ChoirManagement/ChoirDisplay';
import LeadershipDisplay from './LeadershipManagement/LeadershipDisplay';
import { GalleryEventDisplay } from './GalleryManagement';

// Define interface for school data
interface School {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  // Add other properties as needed
}

// Define interface for user data
interface User {
  id: string;
  username: string;
  role: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  token?: string;
}

// Define interface for volunteer status response
interface VolunteerStatus {
  isVolunteering: boolean;
  authenticated: boolean;
  userId?: string;
}

const ChorusHomepage = () => {
  const navigate = useNavigate();

  // Add refs to prevent duplicate API calls in StrictMode
  const eventsLoadedRef = useRef(false);
  const headlinesLoadedRef = useRef(false);
  const schoolLoadedRef = useRef(false);
  const remindersLoadedRef = useRef(false);
  const achievementsLoadedRef = useRef(false);
  const quickAccessLoadedRef = useRef(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [school, setSchool] = useState<School | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [headlines, setHeadlines] = useState<Headline[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [quickAccess, setQuickAccess] = useState<QuickAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  // Volunteer state
  const [volunteerStatuses, setVolunteerStatuses] = useState<Record<string, VolunteerStatus>>({});
  const [volunteerLoading, setVolunteerLoading] = useState<Record<string, boolean>>({});

  // Admin dropdown state
  const [showAdminDropdown, setShowAdminDropdown] = useState<boolean>(false);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Hash fragment state for section navigation
  const [activeSection, setActiveSection] = useState<string>('home');

  // Reference for the admin dropdown - updated to HTMLLIElement to match usage
  const adminDropdownRef = React.useRef<HTMLLIElement | null>(null);

  const slides = [
    {
      title: "Welcome to Harmony High Chorus",
      content: "Discover the joy of music through our award-winning choral program. Join us for an unforgettable musical journey!",
      cta: "Explore Our Choirs",
      link: "#choirs"
    },
    {
      title: "Show Choir Excellence",
      content: "Our competitive show choir just won 1st place at State Championships! Experience the thrill of performance.",
      cta: "See Our Wins",
      link: "#achievements"
    },
    {
      title: "Spring Concert Coming Soon",
      content: "Join us for our biggest performance of the year featuring all our choirs in one spectacular evening.",
      cta: "Get Details",
      link: "#events"
    }
  ];



  // Volunteer management functions - wrapped in useCallback to prevent re-renders
  const fetchVolunteerStatus = useCallback(async (eventId: string) => {
    try {
      const headers: Record<string, string> = {};
      if (user?.token) {
        headers['Authorization'] = `Bearer ${user.token}`;
      }

      const response = await fetch(`${apiConfig.baseURL}/api/public/events/${eventId}/volunteer/status`, {
        headers
      });

      if (response.ok) {
        const status: VolunteerStatus = await response.json();
        setVolunteerStatuses(prev => ({ ...prev, [eventId]: status }));
      }
    } catch (err) {
      console.error('Failed to fetch volunteer status:', err);
    }
  }, [user?.token]);

  const handleVolunteerClick = async (event: Event) => {
    // If user is not logged in, redirect to login
    if (!isLoggedIn || !user) {
      // Store current URL to return to after login
      const currentUrl = window.location.pathname;
      localStorage.setItem('redirectAfterLogin', currentUrl);
      navigate('/login');
      return;
    }

    // If event doesn't need volunteers, show message
    if (!event.volunteersNeeded) {
      alert('This event is not currently seeking volunteers.');
      return;
    }

    const currentStatus = volunteerStatuses[event.id];
    const isCurrentlyVolunteering = currentStatus?.isVolunteering || false;

    // Set loading state
    setVolunteerLoading(prev => ({ ...prev, [event.id]: true }));

    try {
      const headers = {
        'Authorization': `Bearer ${user.token}`,
        'Content-Type': 'application/json'
      };

      if (isCurrentlyVolunteering) {
        // Remove volunteer
        const response = await fetch(`${apiConfig.baseURL}/api/public/events/${event.id}/volunteer`, {
          method: 'DELETE',
          headers
        });

        if (response.ok) {
          const updatedEvent: Event = await response.json();
          // Update events list with new volunteer count
          setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
          // Update volunteer status
          setVolunteerStatuses(prev => ({
            ...prev,
            [event.id]: { ...prev[event.id], isVolunteering: false }
          }));
          alert('You have been removed from the volunteer list.');
        } else {
          const errorText = await response.text();
          alert(`Failed to remove volunteer: ${errorText}`);
        }
      } else {
        // Add volunteer
        const response = await fetch(`${apiConfig.baseURL}/api/public/events/${event.id}/volunteer`, {
          method: 'POST',
          headers
        });

        if (response.ok) {
          const updatedEvent: Event = await response.json();
          // Update events list with new volunteer count
          setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
          // Update volunteer status
          setVolunteerStatuses(prev => ({
            ...prev,
            [event.id]: { ...prev[event.id], isVolunteering: true }
          }));
          alert(`You're now volunteering for ${event.title}!`);
        } else {
          const errorText = await response.text();
          if (response.status === 400) {
            alert('Unable to add volunteer - the event may be full or you may already be volunteering.');
          } else {
            alert(`Failed to add volunteer: ${errorText}`);
          }
        }
      }
    } catch (err) {
      console.error('Volunteer action failed:', err);
      alert('An error occurred. Please try again.');
    } finally {
      // Clear loading state
      setVolunteerLoading(prev => ({ ...prev, [event.id]: false }));
    }
  };

  // Get volunteer button text and style
  const getVolunteerButtonProps = (event: Event) => {
    if (!event.volunteersNeeded) {
      return {
        text: 'No Volunteers Needed',
        className: 'bg-gray-400 text-white px-4 py-2 rounded cursor-not-allowed',
        disabled: true
      };
    }

    const currentStatus = volunteerStatuses[event.id];
    const isCurrentlyVolunteering = currentStatus?.isVolunteering || false;
    const isLoading = volunteerLoading[event.id] || false;
    const currentVolunteers = event.volunteers?.length || 0;
    const maxVolunteers = event.maxVolunteers || 0;
    const isFull = maxVolunteers > 0 && currentVolunteers >= maxVolunteers;

    if (isLoading) {
      return {
        text: 'Loading...',
        className: 'bg-gray-400 text-white px-4 py-2 rounded cursor-not-allowed',
        disabled: true
      };
    }

    if (!isLoggedIn) {
      return {
        text: 'Login to Volunteer',
        className: 'bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded transition-colors',
        disabled: false
      };
    }

    if (isCurrentlyVolunteering) {
      return {
        text: 'Cancel Volunteering',
        className: 'bg-warning-500 hover:bg-warning-600 text-white px-4 py-2 rounded transition-colors',
        disabled: false
      };
    }

    if (isFull) {
      return {
        text: 'Volunteer Spots Full',
        className: 'bg-gray-400 text-white px-4 py-2 rounded cursor-not-allowed',
        disabled: true
      };
    }

    return {
      text: 'Volunteer to Help',
      className: 'bg-success-500 hover:bg-success-600 text-white px-4 py-2 rounded transition-colors',
      disabled: false
    };
  };

  // Create dynamic slides after school data is loaded
  const dynamicSlides = school ? [
    {
      title: `Welcome to ${school.name} Chorus`,
      content: "Discover the joy of music through our award-winning choral program. Join us for an unforgettable musical journey!",
      cta: "Explore Our Choirs",
      link: "#choirs"
    },
    {
      title: "Show Choir Excellence",
      content: "Our competitive show choir just won 1st place at State Championships! Experience the thrill of performance.",
      cta: "See Our Wins",
      link: "#achievements"
    },
    {
      title: "Spring Concert Coming Soon",
      content: "Join us for our biggest performance of the year featuring all our choirs in one spectacular evening.",
      cta: "Get Details",
      link: "#events"
    }
  ] : slides;

  // Use headlines if available, otherwise fall back to dynamic slides
  const displaySlides = headlines.length > 0 ? headlines : dynamicSlides;

  // Fetch school data
  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        const data = await apiClient.get('/api/public/school');
        setSchool(data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch school data:", err);
        setError("Failed to load school data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch school data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!schoolLoadedRef.current) {
      fetchSchoolData();
      schoolLoadedRef.current = true;
    }
  }, []);

  // Fetch events data
  useEffect(() => {
    const fetchEventsData = async () => {
      try {
        const data = await apiClient.get('/api/public/events');
        setEvents(data);
      } catch (err) {
        console.error("Failed to fetch events data:", err);
        // Don't set error state here to keep showing the page even if events fail to load
        setEvents([]); // Set empty events array so UI doesn't break
      }
    };

    // Only fetch events data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!eventsLoadedRef.current) {
      fetchEventsData();
      eventsLoadedRef.current = true;
    }
  }, []);

  // Fetch headlines data
  useEffect(() => {
    const fetchHeadlinesData = async () => {
      try {
        const data = await apiClient.get('/api/public/headlines');
        setHeadlines(data);
      } catch (err) {
        console.error("Failed to fetch headlines data:", err);
        // Don't set error state here to keep showing the page even if headlines fail to load
        setHeadlines([]); // Set empty headlines array so UI doesn't break
      }
    };

    // Only fetch headlines data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!headlinesLoadedRef.current) {
      fetchHeadlinesData();
      headlinesLoadedRef.current = true;
    }
  }, []);

  // Fetch reminders data
  useEffect(() => {
    const fetchRemindersData = async () => {
      try {
        const data = await apiClient.get('/api/public/reminders');

        // Additional client-side filter to ensure only future reminders are shown
        const now = new Date();
        const futureReminders = data.filter((reminder: Reminder) => {
          const dueDate = new Date(reminder.dueDateTime);
          return dueDate > now && !reminder.completed;
        });

        console.log(`DEBUG: Received ${data.length} reminders from backend, filtered to ${futureReminders.length} future reminders`);
        setReminders(futureReminders);
      } catch (err) {
        console.error("Failed to fetch reminders data:", err);
        // Don't set error state here to keep showing the page even if reminders fail to load
        setReminders([]); // Set empty reminders array so UI doesn't break
      }
    };

    // Only fetch reminders data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!remindersLoadedRef.current) {
      fetchRemindersData();
      remindersLoadedRef.current = true;
    }
  }, []);

  // Fetch achievements data
  useEffect(() => {
    const fetchAchievementsData = async () => {
      try {
        const data = await apiClient.get('/api/achievements');
        setAchievements(data);
      } catch (err) {
        console.error("Failed to fetch achievements data:", err);
        // Don't set error state here to keep showing the page even if achievements fail to load
        setAchievements([]); // Set empty achievements array so UI doesn't break
      }
    };

    // Only fetch achievements data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!achievementsLoadedRef.current) {
      fetchAchievementsData();
      achievementsLoadedRef.current = true;
    }
  }, []);

  // Fetch quick access items data
  useEffect(() => {
    const fetchQuickAccessData = async () => {
      try {
        const data = await apiClient.get('/api/public/quick-access');
        setQuickAccess(data);
      } catch (err) {
        console.error("Failed to fetch quick access data:", err);
        // Don't set error state here to keep showing the page even if quick access items fail to load
        setQuickAccess([]); // Set empty quick access array so UI doesn't break
      }
    };

    // Only fetch quick access data if not already loaded (to prevent duplicate calls in StrictMode)
    if (!quickAccessLoadedRef.current) {
      fetchQuickAccessData();
      quickAccessLoadedRef.current = true;
    }
  }, []);

  // Fetch volunteer statuses for all events when events are loaded or user changes
  useEffect(() => {
    if (events.length > 0) {
      events.forEach(event => {
        if (event.volunteersNeeded) {
          fetchVolunteerStatus(event.id);
        }
      });
    }
  }, [events, fetchVolunteerStatus]);

  // Auto-advance carousel - updated to use displaySlides
  useEffect(() => {
    if (displaySlides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % displaySlides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [displaySlides.length]);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('Stored user data:', userData);

        // If the stored user doesn't have a role, try to extract it from the JWT token
        let userRole = userData.role;
        if (!userRole && userData.token) {
          try {
            // Decode JWT token to extract roles
            const tokenParts = userData.token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              console.log('JWT payload:', payload);

              if (payload.roles && payload.roles.length > 0) {
                userRole = payload.roles[0];
                // Remove ROLE_ prefix if present
                if (userRole && userRole.startsWith('ROLE_')) {
                  userRole = userRole.substring(5);
                }

                // Update the stored user data with the extracted role
                const updatedUserData = { ...userData, role: userRole };
                localStorage.setItem('user', JSON.stringify(updatedUserData));
                console.log('Updated user data with role from JWT:', updatedUserData);
                setUser(updatedUserData);
                setIsLoggedIn(true);
                return;
              }
            }
          } catch (jwtError) {
            console.error('Error decoding JWT token:', jwtError);
          }
        }

        console.log('User role:', userData.role);
        console.log('Role check (ADMIN):', userData.role?.toUpperCase() === 'ADMIN');
        console.log('Role check (TEACHER):', userData.role?.toUpperCase() === 'TEACHER');
        setUser(userData);
        setIsLoggedIn(true);
      } catch (err) {
        // Invalid stored data, clear it
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Handle hash fragment changes for section navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the # symbol
      setActiveSection(hash || 'home');
    };

    // Set initial section based on current hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);


  // Function to handle logout
  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
    setIsLoggedIn(false);

    // Redirect to homepage after logout
    window.location.href = '/';
  };

  // Toggle admin dropdown
  const toggleAdminDropdown = () => {
    setShowAdminDropdown(!showAdminDropdown);
  };

  // Close admin dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setShowAdminDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // If still loading, show a loading message
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-600 mb-4">Loading...</div>
          <p className="text-secondary-600">Please wait while we fetch the latest information.</p>
        </div>
      </div>
    );
  }

  // If there was an error, show an error message
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg">
          <div className="text-2xl font-bold text-error-600 mb-4">Something went wrong</div>
          <p className="text-secondary-600 mb-4">{error}</p>
          <button
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Navigation */}
      <nav className="bg-secondary-800 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 text-xl font-bold">
            <div className="text-xl font-bold">🎵 {school?.name || "Chorus Management"}</div>
          </div>

          <ul className="hidden md:flex space-x-8">
            <li><a href="/" className={`hover:text-primary-400 transition-colors ${activeSection === 'home' ? 'text-primary-400' : ''}`}>Home</a></li>
            <li><a href="#choirs" className={`hover:text-primary-400 transition-colors ${activeSection === 'choirs' ? 'text-primary-400' : ''}`}>Our Choirs</a></li>
            <li><a href="#leadership" className={`hover:text-primary-400 transition-colors ${activeSection === 'leadership' ? 'text-primary-400' : ''}`}>Leadership</a></li>
            <li><a href="#gallery" className={`hover:text-primary-400 transition-colors ${activeSection === 'gallery' ? 'text-primary-400' : ''}`}>Gallery</a></li>

            {/* Admin dropdown in main navigation - visible only to admin users */}
            {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
              <li className="relative" ref={adminDropdownRef}>
                <button
                  onClick={toggleAdminDropdown}
                  className="hover:text-primary-400 transition-colors flex items-center"
                >
                  Admin
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showAdminDropdown && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-xl z-50 transition-all duration-200 ease-in-out text-secondary-800">
                    <a href="/admin/choirs" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Choirs
                    </a>
                    <a href="/admin/events" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Events
                    </a>
                    <a href="/admin/reminders" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Reminders
                    </a>
                    <a href="/admin/headlines" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Headlines
                    </a>
                    <a href="/admin/leadership" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Leadership
                    </a>
                    <a href="/admin/gallery" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Gallery
                    </a>
                    <a href="/admin/achievements" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Achievements
                    </a>
                    <a href="/admin/quick-access" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      Manage Quick Access
                    </a>
                    {/* Show school management for ADMIN and TEACHER roles */}
                    {(user?.role?.toUpperCase() === 'ADMIN' || user?.role?.toUpperCase() === 'TEACHER') && (
                      <a href="/admin/school" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                        Manage Schools
                      </a>
                    )}
                    <a href="/admin/users" className="block px-4 py-2 hover:bg-primary-50 transition-colors text-primary-600 font-medium">
                      Manage Users
                    </a>
                    <a href="/admin/approvals" className="block px-4 py-2 hover:bg-primary-50 transition-colors">
                      User Approvals
                    </a>
                  </div>
                )}
              </li>
            )}
          </ul>

          <div className="flex items-center space-x-4 ml-8">
            {/* Mobile menu button - only visible on small screens */}
            <button
              className="md:hidden flex items-center text-white hover:text-primary-400 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {!isLoggedIn ? (
              <>
                <a href="/login" className="px-4 py-2 border border-white rounded hover:bg-white hover:text-secondary-800 transition-all">
                  Member Login
                </a>
                <a href="/join-us" className="px-4 py-2 bg-primary-500 rounded hover:bg-primary-600 transition-all">
                  Join Us
                </a>
              </>
            ) : (
              <button
                onClick={handleLogout}
                className="px-4 py-2 border border-white rounded hover:bg-white hover:text-secondary-800 transition-all"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Hidden by default, toggled by button above */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-secondary-800 text-white shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-2">
            <a href="/" className={`block px-4 py-2 rounded hover:bg-primary-600 transition-colors ${activeSection === 'home' ? 'bg-primary-600' : ''}`}>Home</a>
            <a href="#choirs" className={`block px-4 py-2 rounded hover:bg-primary-600 transition-colors ${activeSection === 'choirs' ? 'bg-primary-600' : ''}`}>Our Choirs</a>
            <a href="#leadership" className={`block px-4 py-2 rounded hover:bg-primary-600 transition-colors ${activeSection === 'leadership' ? 'bg-primary-600' : ''}`}>Leadership</a>
            <a href="#gallery" className={`block px-4 py-2 rounded hover:bg-primary-600 transition-colors ${activeSection === 'gallery' ? 'bg-primary-600' : ''}`}>Gallery</a>

            {/* Admin links - visible only to admin users */}
            {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
              <div className="mt-4">
                <a href="/admin/choirs" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Choirs
                </a>
                <a href="/admin/events" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Events
                </a>
                <a href="/admin/reminders" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Reminders
                </a>
                <a href="/admin/headlines" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Headlines
                </a>
                <a href="/admin/leadership" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Leadership
                </a>
                <a href="/admin/gallery" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Gallery
                </a>
                <a href="/admin/achievements" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Achievements
                </a>
                <a href="/admin/quick-access" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  Manage Quick Access
                </a>
                {/* Show school management for ADMIN and TEACHER roles */}
                {(user?.role?.toUpperCase() === 'ADMIN' || user?.role?.toUpperCase() === 'TEACHER') && (
                  <a href="/admin/school" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                    Manage Schools
                  </a>
                )}
                <a href="/admin/users" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors text-primary-600 font-medium">
                  Manage Users
                </a>
                <a href="/admin/approvals" className="block px-4 py-2 rounded hover:bg-primary-600 transition-colors">
                  User Approvals
                </a>
              </div>
            )}

            {/* Close button */}
            <div className="mt-4">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full px-4 py-2 bg-primary-600 rounded hover:bg-primary-700 transition-colors"
              >
                Close Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conditional Content Based on Active Section */}
      {activeSection === 'choirs' ? (
        /* Choirs Full Page View */
        <>
          <ChoirDisplay />
        </>
      ) : activeSection === 'leadership' ? (
        /* Leadership Full Page View */
        <LeadershipDisplay />
      ) : activeSection === 'gallery' ? (
        /* Gallery Full Page View */
        <GalleryEventDisplay />
      ) : (
        /* Default Home Page View */
        <>
          {/* Hero Carousel */}
          <section className="relative h-80 md:h-96 overflow-hidden mb-8">
            {displaySlides.map((item, index) => {
              // Check if item is a headline or a slide - improved detection
              const isHeadline = headlines.length > 0; // If we have headlines, we're showing headlines
              const title = item.title;
              const content = item.content;

              return (
                <div
                  key={isHeadline ? (item as Headline).id : index}
                  className={`absolute inset-0 bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-center transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {/* Background watermark logo */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: 'url(/school-logo.png)',
                      backgroundSize: 'auto 100%',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.35
                    }}
                  ></div>

                  <div className="max-w-2xl px-8 relative z-10">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 drop-shadow-lg">{title}</h1>
                    <p className="text-lg md:text-xl mb-6 md:mb-8 opacity-90">{content}</p>
                    {/* Only show button for slides, not headlines */}
                    {!isHeadline && (
                      <a
                        href={(item as any).link}
                        className="inline-block bg-primary-600 hover:bg-primary-700 px-6 md:px-8 py-2 md:py-3 rounded-full text-base md:text-lg font-semibold transition-all transform hover:-translate-y-1 hover:shadow-xl"
                      >
                        {(item as any).cta}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Navigation Dots */}
            {displaySlides.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {displaySlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? 'bg-white shadow-lg scale-110'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Main Content */}
          <div className="max-w-6xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
            {/* Events Section */}
            <section className="lg:col-span-2 bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-secondary-800 text-center mb-6 border-b-4 border-primary-500 pb-2 flex-1">
                  Upcoming Events
                </h2>
                {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
                  <a
                    href="/admin/events"
                    className="text-primary-600 hover:text-primary-800 transition-colors ml-4"
                    title="Manage Events"
                  >
                    <Plus size={20} />
                  </a>
                )}
              </div>

              <div className="space-y-6">
                {events.length === 0 ? (
                  <p className="text-center text-secondary-500 py-4">No upcoming events found. Check back later!</p>
                ) : (
                  events.map((event, index) => {
                    const buttonProps = getVolunteerButtonProps(event);
                    const currentVolunteers = event.volunteers?.length || 0;
                    const maxVolunteers = event.maxVolunteers || 0;

                    return (
                      <div key={index} className="flex flex-col md:flex-row bg-secondary-50 p-6 rounded-xl border-l-4 border-primary-500 hover:shadow-lg hover:translate-x-1 transition-all">
                        <div className="bg-primary-500 text-white p-4 rounded-xl text-center mb-4 md:mb-0 md:mr-6 min-w-20">
                          <div className="text-sm uppercase">{new Date(event.startDateTime).toLocaleString('default', { month: 'short' })}</div>
                          <div className="text-2xl font-bold">{new Date(event.startDateTime).getDate()}</div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-secondary-800 mb-2">{event.title}</h3>
                          <p className="text-secondary-600 mb-1"><strong>🕐 Time:</strong> {new Date(event.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(event.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>

                          {/* Enhanced Location with Map Pin */}
                          <div className="text-secondary-600 mb-1">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <strong>Location:</strong>
                                <span className="ml-1">{getLocationDisplayName(event.location)}</span>
                                {getLocationAddress(event.location) && (
                                  <div className="text-sm text-secondary-500 ml-16 mt-1">{getLocationAddress(event.location)}</div>
                                )}
                              </div>
                              {generateMapsUrl(event.location) && (
                                <button
                                  onClick={() => window.open(generateMapsUrl(event.location), '_blank')}
                                  className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                                  title="Open in maps"
                                >
                                  <MapPin size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          <p className="text-secondary-600 mb-2">{event.description}</p>

                          {/* Volunteer information */}
                          {event.volunteersNeeded && (
                            <div className="mb-3">
                              {event.volunteerDescription && (
                                <p className="text-sm text-secondary-600 mb-1">
                                  <strong>Volunteer Help:</strong> {event.volunteerDescription}
                                </p>
                              )}
                              <p className="text-sm text-secondary-600">
                                <strong>Volunteers:</strong> {currentVolunteers}
                                {maxVolunteers > 0 && ` / ${maxVolunteers}`}
                              </p>
                            </div>
                          )}

                          {/* Volunteer button */}
                          {event.volunteersNeeded && (
                            <button
                              onClick={() => handleVolunteerClick(event)}
                              className={buttonProps.className}
                              disabled={buttonProps.disabled}
                            >
                              {buttonProps.text}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {/* Sidebar */}
            <aside className="space-y-8">
              {/* Upcoming Reminders */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-secondary-800 text-center mb-6 border-b-4 border-primary-500 pb-2">
                    Upcoming Reminders
                  </h3>
                  {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
                    <div className="flex justify-end -mt-10 mb-4">
                      <a
                        href="/admin/reminders"
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                        title="Manage Reminders"
                      >
                        <Plus size={20} />
                      </a>
                    </div>
                  )}
                </div>
                {reminders.length === 0 ? (
                  <p className="text-center text-secondary-500 py-4">No upcoming reminders</p>
                ) : (
                  <ul className="space-y-4">
                    {reminders.slice(0, 5).map((reminder) => {
                      const dueDate = new Date(reminder.dueDateTime);
                      const now = new Date();
                      const isOverdue = dueDate < now && !reminder.completed;
                      const isPriority = reminder.priority === 'HIGH' || reminder.priority === 'URGENT';

                      return (
                        <li key={reminder.id} className="flex items-start space-x-3 pb-4 border-b border-secondary-200 last:border-b-0">
                          <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                            reminder.priority === 'URGENT' ? 'bg-red-500' :
                            reminder.priority === 'HIGH' ? 'bg-orange-500' :
                            reminder.priority === 'MEDIUM' ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}></div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-secondary-800 truncate text-left ${
                              reminder.completed ? 'line-through text-gray-500' : ''
                            } ${isOverdue ? 'text-red-600' : ''} ${isPriority ? 'text-orange-700' : ''}`}>
                              {reminder.title}
                            </div>
                            <div className="text-sm text-secondary-600 mt-1 text-left">
                              <div className={`flex items-center ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                                <Calendar size={12} className="mr-1" />
                                <span>
                                  {dueDate.toLocaleDateString()} at {dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                            {reminder.description && (
                              <p className="text-sm text-secondary-600 mt-1 line-clamp-2">
                                {reminder.description}
                              </p>
                            )}
                            {reminder.category && (
                              <span className="inline-block px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700 mt-2">
                                {reminder.category}
                              </span>
                            )}
                            {isOverdue && !reminder.completed && (
                              <div className="text-xs text-red-600 font-medium mt-1">
                                Overdue
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {reminders.length > 5 && (
                  <div className="text-center mt-4">
                    <span className="text-sm text-secondary-500">
                      Showing 5 of {reminders.length} reminders
                    </span>
                  </div>
                )}
              </div>

              {/* Recent Achievements */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-secondary-800 text-center mb-6 border-b-4 border-primary-500 pb-2">
                    Recent Achievements
                  </h3>
                  {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
                    <div className="flex justify-end -mt-10 mb-4">
                      <a
                        href="/admin/achievements"
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                        title="Manage Achievements"
                      >
                        <Plus size={20} />
                      </a>
                    </div>
                  )}
                </div>
                {achievements.length === 0 ? (
                  <p className="text-center text-secondary-500 py-4">No achievements to display yet.</p>
                ) : (
                  <ul className="space-y-4">
                    {achievements.slice(0, 5).map((achievement) => (
                      <li key={achievement.id} className="flex items-center space-x-4 pb-4 border-b border-secondary-200 last:border-b-0">
                        <div className="bg-primary-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">
                          🏆
                        </div>
                        <div>
                          <div className="font-semibold text-secondary-800 text-left">{achievement.title}</div>
                          <div className="text-sm text-secondary-600 text-left">{achievement.month} {achievement.year}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {achievements.length > 5 && (
                  <div className="text-center mt-4">
                    <span className="text-sm text-secondary-500">
                      Showing 5 of {achievements.length} achievements
                    </span>
                  </div>
                )}
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-secondary-800 text-center mb-6 border-b-4 border-primary-500 pb-2">
                    Quick Access
                  </h3>
                  {isLoggedIn && user?.role && (user.role.toUpperCase() === 'ADMIN' || user.role.toUpperCase() === 'TEACHER') && (
                    <div className="flex justify-end -mt-10 mb-4">
                      <a
                        href="/admin/quick-access"
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                        title="Manage Quick Access"
                      >
                        <Plus size={20} />
                      </a>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {quickAccess.length === 0 ? (
                    <p className="text-center text-secondary-500 py-4">No quick access links available</p>
                  ) : (
                    quickAccess.map((item) => (
                      <a
                        key={item.id}
                        href={item.url}
                        className="block bg-primary-500 hover:bg-primary-600 text-white text-center py-3 rounded-full transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title}
                      </a>
                    ))
                  )}
                </div>
              </div>

              {/* Director's Message */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-secondary-800 mb-6 border-b-4 border-primary-500 pb-2">
                  Director's Corner
                </h3>
                <p className="italic text-secondary-700 mb-4">
                  "Welcome to another exciting year of music-making at {school?.name || 'our school'}! I'm thrilled to see our program continue to grow and excel..."
                </p>
                <small className="text-secondary-600">- Ms. Berggren, Choir Director</small>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

export default ChorusHomepage;

